import structuredIfraData from "../data/structured_ifra_data.reconciled.json";
import {
  unfoldFormula,
  aggregateConstituents,
  resolveComposition,
} from "./oilComposition";
import {
  buildParsedResponseFromFormulaText,
  performFuzzySearchAndCheck,
} from "../utils/fuzzySearchUtils";
import { EU_RULESET_MINIMUMS, resolveEuRuleSet } from "./euRuleSet";
import { evaluateMocraCompliance } from "./mocra";

// EU fragrance allergen list — Regulation (EC) 1223/2009 Annex III as amended by
// Regulation (EU) 2023/1545 (expanded allergen disclosure, effective 2026-03-01
// for new products, 2028-03-01 for existing).
//
// DATA PROVENANCE:
//   Original 26 allergens: Annex III entries 45–66, Reg (EC) 1223/2009
//   2023 expansion:        Annex III entries 67–111, Reg (EU) 2023/1545
//   Prohibited substances: HICC → Annex II §1228 via Reg (EU) 2021/1408
//                           Lilial → Annex II §1297 via Reg (EU) 2021/1902
//                           Atranol → Annex II §1329 via Reg (EU) 2023/1545
//                           Chloroatranol → Annex II §1330 via Reg (EU) 2023/1545
//   CAS numbers verified against: IFRA 51st Amendment (2023), EUR-Lex consolidated
//   Last manual review: 2026-04-09
//
// Declaration required on the EU label when > 0.001 % in leave-on, > 0.01 % in rinse-off
const ALLERGEN_SOURCE_URL = "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02009R1223-20240424";
const ALLERGEN_SOURCE_QUOTE =
  "The presence of the substance shall be indicated in the list of ingredients referred to in Article 19(1)(g) when its concentration exceeds 0,001 % in leave-on products and 0,01 % in rinse-off products. Regulation (EC) No 1223/2009, Annex III.";
const ALLERGEN_2023_SOURCE = "Reg. (EU) 2023/1545, Annex III";
const ALLERGEN_2023_URL = "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32023R1545";
const ALLERGEN_2023_QUOTE =
  "Fragrance allergens listed in Annex III to Regulation (EC) No 1223/2009, as amended by Regulation (EU) 2023/1545. Mandatory disclosure from 1 March 2026 (new products) / 1 March 2028 (existing products).";

export const euAllergens = [
  { name: "amyl cinnamal",                              cas: "122-40-7",   inciName: "Amyl Cinnamal",                              source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "amylcinnamyl alcohol",                       cas: "101-85-9",   inciName: "Amylcinnamyl Alcohol",                       source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "anise alcohol",                              cas: "105-13-5",   inciName: "Anise Alcohol",                              source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "anisyl alcohol",                             cas: "105-13-5",   inciName: "Anise Alcohol",                              source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "benzyl alcohol",                             cas: "100-51-6",   inciName: "Benzyl Alcohol",                             source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "benzyl benzoate",                            cas: "120-51-4",   inciName: "Benzyl Benzoate",                            source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "benzyl cinnamate",                           cas: "103-41-3",   inciName: "Benzyl Cinnamate",                           source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "benzyl salicylate",                          cas: "118-58-1",   inciName: "Benzyl Salicylate",                          source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "cinnamaldehyde",                             cas: "104-55-2",   inciName: "Cinnamal",                                   source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "cinnamal",                                   cas: "104-55-2",   inciName: "Cinnamal",                                   source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "cinnamyl alcohol",                           cas: "104-54-1",   inciName: "Cinnamyl Alcohol",                           source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "citral",                                     cas: "5392-40-5",  inciName: "Citral",                                     source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "citronellol",                                cas: "106-22-9",   inciName: "Citronellol",                                source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "coumarin",                                   cas: "91-64-5",    inciName: "Coumarin",                                   source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "eugenol",                                    cas: "97-53-0",    inciName: "Eugenol",                                    source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "farnesol",                                   cas: "4602-84-0",  inciName: "Farnesol",                                   source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "geraniol",                                   cas: "106-24-1",   inciName: "Geraniol",                                   source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "hexyl cinnamaldehyde",                       cas: "101-86-0",   inciName: "Hexyl Cinnamal",                             source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "hexyl cinnamal",                             cas: "101-86-0",   inciName: "Hexyl Cinnamal",                             source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "hydroxycitronellal",                         cas: "107-75-5",   inciName: "Hydroxycitronellal",                         source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "hydroxyisohexyl 3-cyclohexene carboxaldehyde", cas: "31906-04-4", inciName: "Hydroxyisohexyl 3-Cyclohexene Carboxaldehyde", source: "Reg. (EU) 2021/1408, Annex II §1228 (prohibited)", sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32021R1408", sourceQuote: "Hydroxyisohexyl 3-cyclohexene carboxaldehyde (HICC) is prohibited in leave-on cosmetic products. Commission Regulation (EU) 2021/1408 — entry 1228." },
  { name: "hicc",                                       cas: "31906-04-4", inciName: "Hydroxyisohexyl 3-Cyclohexene Carboxaldehyde", source: "Reg. (EU) 2021/1408, Annex II §1228 (prohibited)", sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32021R1408", sourceQuote: "Hydroxyisohexyl 3-cyclohexene carboxaldehyde (HICC) is prohibited in leave-on cosmetic products. Commission Regulation (EU) 2021/1408 — entry 1228." },
  { name: "lyral",                                      cas: "31906-04-4", inciName: "Hydroxyisohexyl 3-Cyclohexene Carboxaldehyde", source: "Reg. (EU) 2021/1408, Annex II §1228 (prohibited)", sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32021R1408", sourceQuote: "Hydroxyisohexyl 3-cyclohexene carboxaldehyde (HICC) is prohibited in leave-on cosmetic products. Commission Regulation (EU) 2021/1408 — entry 1228." },
  { name: "isoeugenol",                                 cas: "97-54-1",    inciName: "Isoeugenol",                                 source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "butylphenyl methylpropional",                cas: "80-54-6",    inciName: "Butylphenyl Methylpropional",                source: "Reg. (EU) 2021/1902, Annex II §1297 (prohibited)", sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32021R1902", sourceQuote: "2-(4-tert-Butylbenzyl)propionaldehyde shall not be used in cosmetic products. Commission Regulation (EU) 2021/1902 — entry 1297. Date of application: 1 March 2022." },
  { name: "lilial",                                     cas: "80-54-6",    inciName: "Butylphenyl Methylpropional",                source: "Reg. (EU) 2021/1902, Annex II §1297 (prohibited)", sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32021R1902", sourceQuote: "2-(4-tert-Butylbenzyl)propionaldehyde shall not be used in cosmetic products. Commission Regulation (EU) 2021/1902 — entry 1297. Date of application: 1 March 2022." },
  { name: "limonene",                                   cas: "138-86-3",   inciName: "Limonene",                                   source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "d-limonene",                                 cas: "5989-27-5",  inciName: "Limonene",                                   source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "linalool",                                   cas: "78-70-6",    inciName: "Linalool",                                   source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "methyl 2-octynoate",                         cas: "111-12-6",   inciName: "Methyl Heptine Carbonate",                   source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "methyl heptine carbonate",                   cas: "111-12-6",   inciName: "Methyl Heptine Carbonate",                   source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "alpha-isomethyl ionone",                     cas: "127-51-5",   inciName: "Alpha-Isomethyl Ionone",                     source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "alpha isomethyl ionone",                     cas: "127-51-5",   inciName: "Alpha-Isomethyl Ionone",                     source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "evernia prunastri extract",                  cas: "90028-68-5", inciName: "Evernia Prunastri Extract",                  source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "oakmoss",                                    cas: "90028-68-5", inciName: "Evernia Prunastri Extract",                  source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "oakmoss absolute",                           cas: "90028-68-5", inciName: "Evernia Prunastri Extract",                  source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "evernia furfuracea extract",                 cas: "90028-67-4", inciName: "Evernia Furfuracea Extract",                 source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "treemoss",                                   cas: "90028-67-4", inciName: "Evernia Furfuracea Extract",                 source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },
  { name: "treemoss absolute",                          cas: "90028-67-4", inciName: "Evernia Furfuracea Extract",                 source: "Reg. (EC) 1223/2009, Annex III", sourceUrl: ALLERGEN_SOURCE_URL, sourceQuote: ALLERGEN_SOURCE_QUOTE },

  // ── 2023 expansion — Regulation (EU) 2023/1545 ──
  // Prohibited substances moved to Annex II
  { name: "atranol",                                    cas: "526-37-4",   inciName: "Atranol",                                    source: "Reg. (EU) 2023/1545, Annex II §1329 (prohibited)", sourceUrl: ALLERGEN_2023_URL, sourceQuote: "Atranol is prohibited in cosmetic products. Commission Regulation (EU) 2023/1545 — entry 1329." },
  { name: "chloroatranol",                              cas: "57074-21-2", inciName: "Chloroatranol",                              source: "Reg. (EU) 2023/1545, Annex II §1330 (prohibited)", sourceUrl: ALLERGEN_2023_URL, sourceQuote: "Chloroatranol is prohibited in cosmetic products. Commission Regulation (EU) 2023/1545 — entry 1330." },
  // New disclosure allergens
  { name: "trans-anethole",                             cas: "4180-23-8",  inciName: "Anise Alcohol",                              source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "anethole",                                   cas: "4180-23-8",  inciName: "Anise Alcohol",                              source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "benzaldehyde",                               cas: "100-52-7",   inciName: "Benzaldehyde",                               source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "camphor",                                    cas: "76-22-2",    inciName: "Camphor",                                    source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "d-camphor",                                  cas: "464-49-3",   inciName: "Camphor",                                    source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "carvacrol",                                  cas: "499-75-2",   inciName: "Carvacrol",                                  source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "carvone",                                    cas: "99-49-0",    inciName: "Carvone",                                    source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "d-carvone",                                  cas: "2244-16-8",  inciName: "Carvone",                                    source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "l-carvone",                                  cas: "6485-40-1",  inciName: "Carvone",                                    source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "beta-caryophyllene",                         cas: "87-44-5",    inciName: "Beta-Caryophyllene",                         source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "caryophyllene",                              cas: "87-44-5",    inciName: "Beta-Caryophyllene",                         source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "citronellal",                                cas: "106-23-0",   inciName: "Citronellal",                                source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "alpha-damascone",                            cas: "43052-87-5", inciName: "Alpha-Damascone",                            source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "beta-damascone",                             cas: "23726-92-3", inciName: "Beta-Damascone",                             source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "delta-damascone",                            cas: "57378-68-4", inciName: "Delta-Damascone",                            source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "damascenone",                                cas: "23696-85-7", inciName: "Damascenone",                                source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "dimethylbenzyl carbinyl butyrate",            cas: "10094-34-5", inciName: "Dimethylbenzyl Carbinyl Butyrate",            source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "hexadecanolactone",                          cas: "109-29-5",   inciName: "Hexadecanolactone",                          source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "menthol",                                    cas: "89-78-1",    inciName: "Menthol",                                    source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "l-menthol",                                  cas: "2216-51-5",  inciName: "Menthol",                                    source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "dl-menthol",                                 cas: "1490-04-6",  inciName: "Menthol",                                    source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "methyl salicylate",                          cas: "119-36-8",   inciName: "Methyl Salicylate",                          source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "alpha-pinene",                               cas: "80-56-8",    inciName: "Alpha-Pinene",                               source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "beta-pinene",                                cas: "127-91-3",   inciName: "Beta-Pinene",                                source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "propylidene phthalide",                      cas: "17369-59-4", inciName: "Propylidene Phthalide",                      source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "rose ketone-4",                              cas: "23726-93-4", inciName: "Damascone",                                  source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "salicylaldehyde",                            cas: "90-02-8",    inciName: "Salicylaldehyde",                            source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "2-hydroxybenzaldehyde",                      cas: "90-02-8",    inciName: "Salicylaldehyde",                            source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "thymol",                                     cas: "89-83-8",    inciName: "Thymol",                                     source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
  { name: "vanillin",                                   cas: "121-33-5",   inciName: "Vanillin",                                   source: ALLERGEN_2023_SOURCE, sourceUrl: ALLERGEN_2023_URL, sourceQuote: ALLERGEN_2023_QUOTE },
];

// Lookup maps — name key is already normalised
const EU_ALLERGEN_BY_NAME = new Map(euAllergens.map((a) => [a.name, a]));
const EU_ALLERGEN_BY_CAS = new Map(
  euAllergens
    .filter((a) => a.cas)
    .map((a) => [a.cas.replace(/\s/g, "").toLowerCase(), a])
);

const requiredPifDocuments = ["CPSR", "GMP Statement", "Claims Substantiation"];

const titleCase = (value) =>
  String(value || "")
    .replace(/\b\w/g, (match) => match.toUpperCase());

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const createIssue = ({
  code,
  severity = "error",
  jurisdiction,
  scope,
  message,
  source = "derived",
  sourceUrl = "",
  sourceQuote = "",
  sourceQuotes = null,
  highlight = null,
  blocking = true,
}) => {
  const issue = {
    code,
    severity,
    jurisdiction,
    scope,
    message,
    source,
    sourceUrl,
    sourceQuote,
    blocking,
  };
  if (Array.isArray(sourceQuotes) && sourceQuotes.length) {
    issue.sourceQuotes = sourceQuotes.filter((entry) => entry && (entry.quote || entry.text));
  }
  if (highlight) {
    issue.highlight = highlight;
  }
  return issue;
};

// ---------------------------------------------------------------------------
// Shared regulation quote fragments, reusable across chemical-specific issues.
// ---------------------------------------------------------------------------
const EU_ARTICLE_14_PROHIBITION_QUOTE = {
  label: "Article 14 · General prohibition",
  source: "Reg. (EC) 1223/2009, Art. 14(1)(a)",
  sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02009R1223-20240424",
  quote:
    "Cosmetic products shall not contain substances listed in Annex II. Member States shall ensure that cosmetic products placed on the market comply with this Regulation.",
};

const EU_ARTICLE_14_RESTRICTED_QUOTE = {
  label: "Article 14 · Restricted substances",
  source: "Reg. (EC) 1223/2009, Art. 14(1)(b)",
  sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02009R1223-20240424",
  quote:
    "Cosmetic products shall not contain substances listed in Annex III outside the framework of the restrictions laid down therein.",
};

const EU_ARTICLE_19_ALLERGEN_QUOTE = {
  label: "Article 19(1)(g) · Labeling disclosure",
  source: "Reg. (EC) 1223/2009, Art. 19(1)(g)",
  sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02009R1223-20240424",
  quote:
    "Cosmetic products shall be made available on the market only where the container and packaging bear a list of ingredients. The presence of substances required to be listed in Annex III shall be indicated in the list of ingredients irrespective of their function in the product.",
};

const IFRA_CATEGORY_4_QUOTE = {
  label: "IFRA Category 4 — Products applied to the body and left on",
  source: "IFRA Standards · Category definitions",
  sourceUrl: "https://ifrafragrance.org/standards-library",
  quote:
    "Category 4 applies to fragrances in hydroalcoholic products applied to the body and left on, including fine fragrance, eau de toilette, eau de cologne, and similar leave-on fragrance products.",
};

const IFRA_GOVERNANCE_QUOTE = {
  label: "IFRA Code of Practice",
  source: "IFRA Code of Practice · Article 1",
  sourceUrl: "https://ifrafragrance.org/safe-use/library",
  quote:
    "IFRA members and the fragrance industry shall ensure that fragrance compounds are in full compliance with the current IFRA Standards. Ingredients that cannot be identified against the IFRA library may not carry a Category 4 threshold confirmation.",
};

const IFRA_CONCENTRATION_UNITS_QUOTE = {
  label: "IFRA concentration declaration",
  source: "IFRA Standards · Quantitative Risk Assessment",
  sourceUrl: "https://ifrafragrance.org/standards-library",
  quote:
    "Ingredient concentration must be declared in a convertible unit (percent, ppm, or mg/g of the finished product) to enable comparison against Category thresholds. Unverified units prevent a threshold verdict from being issued.",
};

const EU_ARTICLE_10_SAFETY_ASSESSMENT_QUOTE = {
  label: "Article 10 · Safety Assessment",
  source: "Reg. (EC) 1223/2009, Art. 10(1)",
  sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02009R1223-20240424",
  quote:
    "In order to demonstrate that a cosmetic product complies with Article 3, the responsible person shall, prior to placing a cosmetic product on the market, ensure that the cosmetic product has undergone a safety assessment on the basis of the relevant information, and that a cosmetic product safety report is set up in accordance with Annex I.",
};

const EU_ANNEX_I_CPSR_QUOTE = {
  label: "Annex I · Cosmetic Product Safety Report",
  source: "Reg. (EC) 1223/2009, Annex I",
  sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02009R1223-20240424",
  quote:
    "The cosmetic product safety report shall, as a minimum, contain: quantitative and qualitative composition, physical/chemical characteristics and stability, microbiological quality, impurities, normal and reasonably foreseeable use, exposure to the product, exposure to substances, toxicological profile, undesirable effects, and conclusions of the assessment.",
};

const EU_ARTICLE_11_PIF_QUOTE = {
  label: "Article 11 · Product Information File",
  source: "Reg. (EC) 1223/2009, Art. 11",
  sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02009R1223-20240424",
  quote:
    "The responsible person shall keep a product information file for the cosmetic product. The product information file shall contain: a description of the cosmetic product, the cosmetic product safety report, a description of manufacturing method, proof of the effect claimed, and data on any animal testing.",
};

const EU_ARTICLE_20_CLAIMS_QUOTE = {
  label: "Article 20 · Product claims",
  source: "Reg. (EC) 1223/2009, Art. 20(1)",
  sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02009R1223-20240424",
  quote:
    "In the labelling, making available on the market and advertising of cosmetic products, text, names, trade marks, pictures and figurative or other signs shall not be used to imply that these products have characteristics or functions which they do not have.",
};

const EU_COSING_DATABASE_QUOTE = {
  label: "CosIng database",
  source: "European Commission · CosIng ingredient database",
  sourceUrl: "https://single-market-economy.ec.europa.eu/sectors/cosmetics/cosmetic-ingredient-database_en",
  quote:
    "The CosIng database contains information on cosmetic substances and ingredients, including regulatory provisions and required INCI nomenclature. All ingredients used in cosmetic products shall be identified using the INCI nomenclature as listed in CosIng.",
};

const EU_ARTICLE_19_INGREDIENT_LIST_QUOTE = {
  label: "Article 19(1)(g) · Ingredient list identity",
  source: "Reg. (EC) 1223/2009, Art. 19(1)(g)",
  sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02009R1223-20240424",
  quote:
    "The list of ingredients shall be established in descending order of weight at the time of their addition to the cosmetic product. The list shall be preceded by the term \"ingredients\".",
};

const SOURCE_CONTINUITY_QUOTE = {
  label: "Source continuity policy",
  source: "Regulatory Tracker · internal policy",
  sourceUrl: "",
  quote:
    "When a previously healthy upstream regulatory source cannot be reached, the system continues to evaluate against the last-known snapshot. Findings based on a stale snapshot should be manually re-verified before release.",
};

const US_FDA_COSMETIC_IDENTITY_QUOTE = {
  label: "FDA · Cosmetic ingredient identity",
  source: "FDA · Cosmetic ingredient overview",
  sourceUrl: "https://www.fda.gov/cosmetics/cosmetic-products-ingredients/cosmetic-ingredients",
  quote:
    "Cosmetic ingredients must be identified by the name assigned in accordance with 21 CFR § 701.3. Ingredients that cannot be matched to an accepted identity cannot be evaluated for compliance with U.S. cosmetic regulations.",
};

const US_MOCRA_SAFETY_SUBSTANTIATION_QUOTE = {
  label: "MoCRA § 608 · Safety substantiation",
  source: "21 USC § 364d",
  sourceUrl: "https://www.fda.gov/cosmetics/cosmetics-laws-regulations/modernization-cosmetics-regulation-act-2022-mocra",
  quote:
    "The responsible person for a cosmetic product shall ensure, and maintain records supporting, that there is adequate substantiation of safety of such cosmetic product. Safety substantiation requires quantified concentration data for all ingredients.",
};

const US_FTC_CLAIMS_QUOTE = {
  label: "FTC · Substantiation of advertising claims",
  source: "FTC Act § 5 / 15 USC § 45",
  sourceUrl: "https://www.ftc.gov/legal-library/browse/ftc-policy-statement-regarding-advertising-substantiation",
  quote:
    "Advertisers must have a reasonable basis for all objective product claims before those claims are disseminated. Claims for cosmetic products must be truthful, non-misleading, and supported by competent and reliable evidence.",
};

// Build a chemical-specific quote for an Annex II/III rule match.
const buildAnnexQuote = (rule, ingredient) => {
  const ingredientName = ingredient?.ingredient_name || rule.inciName || rule.substanceName;
  const annex = rule.annex || "III";
  const refNo = rule.referenceNumber || "";
  const ruleSource = `EUR-Lex Annex ${annex}${refNo ? ` ref ${refNo}` : ""}`;

  if (rule.sourceQuote && rule.sourceQuote.trim()) {
    return {
      label: `Annex ${annex} · entry ${refNo}`,
      source: ruleSource,
      sourceUrl: rule.sourceUrl || "",
      quote: rule.sourceQuote,
    };
  }

  // Fall back to a reconstructed entry line if the snapshot lacks a quote.
  const casLine = (rule.casNumbers || []).length
    ? ` (CAS ${(rule.casNumbers || []).slice(0, 3).join(", ")})`
    : "";
  const limitLine = Number.isFinite(Number(rule.maxPercentage)) && Number(rule.maxPercentage) > 0
    ? ` Maximum concentration in the ready-for-use preparation: ${rule.maxPercentage}%.`
    : "";
  const proh = rule.ruleType === "prohibited"
    ? ` — listed in Annex II and shall not be used in cosmetic products.`
    : ` — listed in Annex III; use is subject to the restrictions set out in the Annex.`;
  return {
    label: `Annex ${annex} · entry ${refNo}`,
    source: ruleSource,
    sourceUrl: rule.sourceUrl || "",
    quote: `${rule.substanceName || ingredientName}${casLine}${proh}${limitLine}`,
  };
};

const buildEuIngredientIndex = (regulatoryIngredients = []) => {
  const byTerm = new Map();
  regulatoryIngredients.forEach((ingredient) => {
    [
      ingredient.canonicalName,
      ingredient.currentInciName,
      ingredient.casNumber,
      ...(ingredient.relatedNames || []),
    ]
      .map(normalizeText)
      .filter(Boolean)
      .forEach((term) => {
        if (!byTerm.has(term)) {
          byTerm.set(term, ingredient);
        }
      });
  });
  return byTerm;
};

const splitCasList = (value) =>
  String(value || "")
    .split(/\s*,\s*/)
    .map((entry) => entry.trim())
    .filter(Boolean);

const buildEuRuleIndex = (regulatoryRules = []) => {
  const byTerm = new Map();
  regulatoryRules
    .filter(
      (rule) =>
        rule.jurisdiction === "EU" &&
        ["II", "III", "IV", "V", "VI"].includes(rule.annex)
    )
    .forEach((rule) => {
      [
        rule.substanceName,
        rule.inciName,
        rule.ciNumber, // Annex IV identifier (e.g. "77891")
        ...(rule.synonyms || []),
        ...(rule.casNumbers || []),
      ]
        .flatMap((term) => splitCasList(term))
        .map(normalizeText)
        .filter(Boolean)
        .forEach((term) => {
          const existing = byTerm.get(term) || [];
          byTerm.set(term, [...existing, rule]);
        });
    });
  return byTerm;
};

// Detect the cosmetic function of an ingredient using the CosIng `fn` field
// (set on cosing-passthrough rows by `fuzzySearchUtils`) plus a name-pattern
// fallback for colorants. Returns "colorant" / "preservative" / "uv-filter"
// when the ingredient belongs to a positive-list-regulated category, else
// null. Used to trigger Annex IV/V/VI positive-list checks.
const detectPositiveListCategory = (ingredient) => {
  const fn = String(
    ingredient?.cosing?.function || ingredient?.function || ""
  ).toLowerCase();
  const name = String(ingredient?.ingredient_name || "").trim();
  // Annex IV — colorants. CosIng calls them "cosmetic colorant" / "hair
  // dyeing" / "hair colorant"; CI-numbered names always pass.
  if (/^ci\s*\d{4,5}\b/i.test(name) || /\bci\s*\d{4,5}\b/i.test(name)) {
    return "colorant";
  }
  if (/cosmetic colorant|hair colorant|hair dyeing|colour additive/.test(fn)) {
    return "colorant";
  }
  // Annex V — preservatives.
  if (/\bpreservative\b/.test(fn)) return "preservative";
  // Annex VI — UV filters.
  if (/uv\s*filter|uv\s*absorb|uv\s*protect|sunscreen/.test(fn)) return "uv-filter";
  return null;
};

// Map a positive-list category to its corresponding Annex letter.
const POSITIVE_LIST_ANNEX = {
  colorant: "IV",
  preservative: "V",
  "uv-filter": "VI",
};

const productTypeTerms = (product) =>
  [product.category, product.function]
    .map(normalizeText)
    .filter(Boolean);

const ruleAppliesToProduct = (rule, product) => {
  const ruleTypes = (rule.productTypes || []).map(normalizeText);
  if (!ruleTypes.length || ruleTypes.includes("all")) {
    return true;
  }

  const terms = productTypeTerms(product);
  return ruleTypes.some((type) => terms.some((term) => term.includes(type)));
};

export const evaluateFormulaFromText = (inputText) => {
  const parsedResponse = buildParsedResponseFromFormulaText(inputText, structuredIfraData);
  const ifraResults = performFuzzySearchAndCheck(parsedResponse, structuredIfraData);

  // Compositional unfolding (SOTA #4) — exposed as a SEPARATE `unfolded`
  // field so EU/CosIng registry matching, the existing compliance issue
  // pipeline, and label INCI generation all keep their oil-row view of
  // the formula. UI surfaces (Risk Matrix, allergen panel) opt into
  // `unfolded` to display constituent-level analysis.
  const unfoldedResults = unfoldFormula(ifraResults);
  const constituentRows = unfoldedResults.filter((r) => r.isComposition);
  const constituentParsed = Object.fromEntries(
    constituentRows.map((row) => [
      row.ingredient_name,
      {
        CAS_NUMBER: row.cas_number,
        formula_percentage: row.formula_percentage,
        concentration_verified: true,
        chemical_name: row.ingredient_name,
      },
    ])
  );
  const constituentChecked = performFuzzySearchAndCheck(
    constituentParsed,
    structuredIfraData
  ).map((row, i) => ({
    ...row,
    isComposition: true,
    derivedFrom: constituentRows[i]?.derivedFrom,
  }));

  return {
    parsedResponse,
    ifraResults,
    unfolded: {
      constituents: constituentChecked,
      aggregates: Array.from(aggregateConstituents(unfoldedResults).values()),
    },
  };
};

const deriveIngredientFacts = (ifraResults) => {
  const validIngredients = ifraResults.filter((item) => item.validIngredient);
  const invalidIngredients = ifraResults.filter((item) => !item.validIngredient);
  const unverifiedConcentrations = ifraResults.filter(
    (item) => item.concentration_verified !== true
  );
  const thresholdBreaches = ifraResults.filter((item) => item.above_threshold);

  const seen = new Set();
  const allergenDetails = validIngredients.reduce((acc, item) => {
    const nameKey = normalizeText(item.ingredient_name);
    const casKey = normalizeText(item.cas_number || "").replace(/\s/g, "");
    const allergen =
      EU_ALLERGEN_BY_NAME.get(nameKey) ||
      (casKey && casKey !== "n/a" ? EU_ALLERGEN_BY_CAS.get(casKey) : null);
    if (allergen) {
      const dedupeKey = `${allergen.cas}-${allergen.inciName}`;
      if (!seen.has(dedupeKey)) {
        seen.add(dedupeKey);
        acc.push(allergen);
      }
    }
    return acc;
  }, []);

  const allergens = allergenDetails.map((a) => titleCase(a.inciName));

  return {
    validIngredients,
    invalidIngredients,
    unverifiedConcentrations,
    thresholdBreaches,
    allergens,
    allergenDetails,
  };
};

const buildIngredientDeclaration = (ifraResults, ingredientNameMap = new Map()) => {
  const valid = ifraResults.filter((item) => item.validIngredient);
  const aboveOnePercent = valid
    .filter((item) => Number(item.formula_percentage) >= 1)
    .sort((left, right) => Number(right.formula_percentage) - Number(left.formula_percentage));
  const belowOnePercent = valid
    .filter((item) => Number(item.formula_percentage) < 1)
    .sort((left, right) =>
      String(left.ingredient_name).localeCompare(String(right.ingredient_name))
    );

  return [...aboveOnePercent, ...belowOnePercent].map((item) => {
    const normalizedName = normalizeText(item.ingredient_name);
    const mappedName = ingredientNameMap.get(normalizedName) || item.ingredient_name;
    return titleCase(mappedName);
  });
};

export const buildProductCompliance = (
  product,
  documents = [],
  {
    regulatoryIngredients = [],
    regulatoryChanges = [],
    regulatorySources = [],
    regulatoryRules = [],
    vendors = [],
  } = {}
) => {
  const activeFormula =
    product.formulaVersions?.find((formula) => formula.id === product.currentFormulaId) ||
    product.formulaVersions?.[0];

  if (!activeFormula) {
    return {
      activeFormula: null,
      jurisdictions: {},
      labelDrafts: {},
      pifDraft: null,
      overallStatus: "Draft",
      issueCount: 0,
      blockingIssueCount: 0,
    };
  }

  const { parsedResponse, ifraResults, unfolded } = activeFormula.parsedResponse
    ? (() => {
        const baseIfra =
          activeFormula.ifraResults ||
          performFuzzySearchAndCheck(activeFormula.parsedResponse, structuredIfraData);
        const unfoldedRows = unfoldFormula(baseIfra);
        const constituentRows = unfoldedRows.filter((r) => r.isComposition);
        const constituentParsed = Object.fromEntries(
          constituentRows.map((row) => [
            row.ingredient_name,
            {
              CAS_NUMBER: row.cas_number,
              formula_percentage: row.formula_percentage,
              concentration_verified: true,
              chemical_name: row.ingredient_name,
            },
          ])
        );
        const constituentChecked = performFuzzySearchAndCheck(
          constituentParsed,
          structuredIfraData
        ).map((row, i) => ({
          ...row,
          isComposition: true,
          derivedFrom: constituentRows[i]?.derivedFrom,
        }));
        return {
          parsedResponse: activeFormula.parsedResponse,
          ifraResults: baseIfra,
          unfolded: {
            constituents: constituentChecked,
            aggregates: Array.from(
              aggregateConstituents(unfoldedRows).values()
            ),
          },
        };
      })()
    : evaluateFormulaFromText(activeFormula.inputText || "");

  const ingredientFacts = deriveIngredientFacts(ifraResults);
  const productDocuments = documents.filter((document) => document.productId === product.id);
  const documentTypes = productDocuments.map((document) => document.type);
  const missingDocuments = (product.requiredDocuments || []).filter(
    (type) => !documentTypes.includes(type)
  );
  const euIngredientIndex = buildEuIngredientIndex(regulatoryIngredients);
  const euRuleSet = resolveEuRuleSet(regulatoryRules);
  const euRuleIndex = buildEuRuleIndex(euRuleSet.rules);
  const euMatches = ingredientFacts.validIngredients.map((ingredient) => {
    const ingredientKey = normalizeText(ingredient.ingredient_name);
    const casKey = normalizeText(ingredient.cas_number);
    const match =
      euIngredientIndex.get(ingredientKey) ||
      (casKey && casKey !== "n/a" ? euIngredientIndex.get(casKey) : null);

    return {
      ingredient,
      match: match || null,
    };
  });
  const euMatchedIngredients = euMatches.filter((entry) => entry.match);
  const euUnmatchedIngredients = euMatches.filter((entry) => !entry.match);
  const euLabelNameMap = new Map(
    euMatchedIngredients.map(({ ingredient, match }) => [
      normalizeText(ingredient.ingredient_name),
      match.currentInciName || match.canonicalName || ingredient.ingredient_name,
    ])
  );
  const labelIngredients = buildIngredientDeclaration(ifraResults, euLabelNameMap);
  const euSourceHealth = regulatorySources
    .filter((source) => ["eurlex", "cosing", "sccs", "safety_gate"].includes(source.sourceType))
    .map((source) => ({
      sourceType: source.sourceType,
      label: source.label,
      status: source.lastSyncStatus || "Pending",
      lastSyncedAt: source.lastSyncedAt || "",
      previouslyHealthy: source.recordCount > 0,
    }));
  const usSourceHealth = regulatorySources
    .filter((source) => String(source.sourceType || "").startsWith("fda_"))
    .map((source) => ({
      sourceType: source.sourceType,
      label: source.label,
      status: source.lastSyncStatus || "Pending",
      lastSyncedAt: source.lastSyncedAt || "",
      previouslyHealthy: Boolean(source.lastSyncedAt),
    }));
  const productTerms = new Set(
    [
      ...ingredientFacts.validIngredients.map((item) => normalizeText(item.ingredient_name)),
      ...euMatchedIngredients.flatMap(({ match }) =>
        [match?.canonicalName, match?.currentInciName, match?.casNumber].map(normalizeText)
      ),
    ].filter(Boolean)
  );
  const relevantEuChanges = regulatoryChanges.filter((change) => {
    const terms = [
      change.entityKey,
      ...(change.relatedNames || []),
    ]
      .map(normalizeText)
      .filter(Boolean);

    return terms.some((term) => productTerms.has(term));
  });
  const annexMatches = ifraResults.flatMap((ingredient) => {
    const terms = [
      ingredient.ingredient_name,
      ingredient.cas_number,
      ...splitCasList(ingredient.cas_number),
    ]
      .map(normalizeText)
      .filter(Boolean);
    const rules = terms.flatMap((term) => euRuleIndex.get(term) || []);
    const uniqueRules = Array.from(new Map(rules.map((rule) => [rule.id, rule])).values());

    return uniqueRules
      .filter((rule) => ruleAppliesToProduct(rule, product))
      .map((rule) => ({
        ingredient,
        rule,
      }));
  });

  // Annex IV / V / VI positive-list violations — ingredients whose cosmetic
  // function (colorant / preservative / UV filter) is one of the positive-
  // list-regulated categories but which did NOT match any rule on that
  // annex. These are NON-permitted under Reg. 1223/2009 Article 14(1) and
  // need to be flagged blocking.
  const positiveListViolations = ifraResults
    .map((ingredient) => {
      const category = detectPositiveListCategory(ingredient);
      if (!category) return null;
      const expectedAnnex = POSITIVE_LIST_ANNEX[category];
      const matchedExpectedAnnex = annexMatches.some(
        (m) => m.ingredient === ingredient && m.rule.annex === expectedAnnex
      );
      if (matchedExpectedAnnex) return null;
      return { ingredient, category, expectedAnnex };
    })
    .filter(Boolean);

  const ifraIssues = [
    ...ingredientFacts.invalidIngredients.map((ingredient) => {
      const chemicalQuote = {
        label: `Unmatched ingredient · ${ingredient.ingredient_name}`,
        source: "IFRA dataset",
        sourceUrl: "https://ifrafragrance.org/standards-library",
        quote: `${ingredient.ingredient_name}${ingredient.cas_number && ingredient.cas_number !== "N/A" ? ` (CAS ${ingredient.cas_number})` : ""} could not be matched to a published IFRA Standard. Without an IFRA record, no Category 4 threshold can be applied.`,
      };
      return createIssue({
        code: "IFRA_INGREDIENT_UNVERIFIED",
        jurisdiction: "IFRA",
        scope: "formula",
        source: "IFRA dataset",
        sourceQuotes: [chemicalQuote, IFRA_GOVERNANCE_QUOTE],
        highlight: [ingredient.ingredient_name, ingredient.cas_number].filter((v) => v && v !== "N/A"),
        message: `${ingredient.ingredient_name} could not be matched to a valid IFRA ingredient.`,
      });
    }),
    ...ingredientFacts.unverifiedConcentrations.map((ingredient) => {
      const chemicalQuote = {
        label: `Unverified concentration · ${ingredient.ingredient_name}`,
        source: "formula input",
        sourceUrl: "",
        quote: `${ingredient.ingredient_name} was declared with an unsupported or missing concentration unit. To evaluate against IFRA Category thresholds, provide the concentration as percent (%), ppm, or mg/g of the finished product.`,
      };
      return createIssue({
        code: "IFRA_CONCENTRATION_UNVERIFIED",
        jurisdiction: "IFRA",
        scope: "formula",
        source: "formula input",
        sourceQuotes: [chemicalQuote, IFRA_CONCENTRATION_UNITS_QUOTE],
        highlight: [ingredient.ingredient_name, ingredient.cas_number].filter((v) => v && v !== "N/A"),
        severity: "error",
        blocking: true,
        message: `${ingredient.ingredient_name} concentration is unverified; provide %, ppm, or mg/g.`,
      });
    }),
    ...ingredientFacts.thresholdBreaches.map((ingredient) => {
      const threshold = ingredient.category_4_threshold;
      const actual = ingredient.formula_percentage;
      const chemicalQuote = {
        label: `IFRA standard · ${ingredient.ingredient_name}`,
        source: ingredient.document_name ? `IFRA Standard — ${ingredient.document_name}` : "IFRA dataset",
        sourceUrl: "https://ifrafragrance.org/standards-library",
        quote: `${ingredient.ingredient_name} (CAS ${ingredient.cas_number || "N/A"}) shall not exceed ${threshold}% in finished fragrance products within IFRA Category 4. Declared concentration in this formula: ${actual}%.`,
      };
      return createIssue({
        code: "IFRA_THRESHOLD_EXCEEDED",
        jurisdiction: "IFRA",
        scope: "formula",
        source: ingredient.document_name || "IFRA dataset",
        sourceQuotes: [chemicalQuote, IFRA_CATEGORY_4_QUOTE],
        highlight: [ingredient.ingredient_name, ingredient.cas_number].filter(Boolean),
        message: `${ingredient.ingredient_name} exceeds the Category 4 threshold.`,
      });
    }),
  ];

  const euIssues = [
    ...(!euRuleSet.complete
      ? [
          createIssue({
            code: "EU_RULESET_UNAVAILABLE",
            jurisdiction: "EU",
            scope: "regulatory",
            source: "EU Annex II/III local snapshot",
            sourceQuotes: [
              {
                label: "Snapshot integrity",
                source: "EU Annex II/III local snapshot",
                sourceUrl: "",
                quote: `The currently loaded EU Annex II/III rule set contains ${euRuleSet.counts.total} entries, below the ${EU_RULESET_MINIMUMS.total}-entry minimum required for a complete evaluation. Re-sync EUR-Lex to restore the full rule set.`,
              },
              EU_ARTICLE_14_PROHIBITION_QUOTE,
              EU_ARTICLE_14_RESTRICTED_QUOTE,
            ],
            severity: "critical",
            blocking: true,
            message: `EU Annex II/III rules unavailable. Loaded ${euRuleSet.counts.total} rules; required at least ${EU_RULESET_MINIMUMS.total}.`,
          }),
        ]
      : []),
    ...(ingredientFacts.invalidIngredients.length
      ? [
          createIssue({
            code: "EU_INGREDIENT_IDENTITY_REVIEW",
            jurisdiction: "EU",
            scope: "formula",
            source: "CosIng / IFRA derived",
            sourceUrl: "https://single-market-economy.ec.europa.eu/sectors/cosmetics/cosmetic-ingredient-database_en",
            sourceQuote: "All ingredients used in cosmetic products shall be identified using the INCI nomenclature as listed in the CosIng database.",
            sourceQuotes: [
              {
                label: `Unmatched ingredients · ${ingredientFacts.invalidIngredients.length}`,
                source: "CosIng / IFRA derived",
                sourceUrl: "https://single-market-economy.ec.europa.eu/sectors/cosmetics/cosmetic-ingredient-database_en",
                quote: `${ingredientFacts.invalidIngredients.length} ingredient${ingredientFacts.invalidIngredients.length === 1 ? "" : "s"} in this formula — ${ingredientFacts.invalidIngredients.slice(0, 5).map((i) => i.ingredient_name).join(", ")}${ingredientFacts.invalidIngredients.length > 5 ? ", …" : ""} — could not be normalized to an INCI identity. EU compliance cannot be evaluated against these ingredients.`,
              },
              EU_COSING_DATABASE_QUOTE,
              EU_ARTICLE_19_INGREDIENT_LIST_QUOTE,
            ],
            highlight: ingredientFacts.invalidIngredients.map((i) => i.ingredient_name),
            message: "Ingredient identity review needed",
          }),
        ]
      : []),
    ...(ingredientFacts.unverifiedConcentrations.length
      ? [
          createIssue({
            code: "EU_CONCENTRATION_UNVERIFIED",
            jurisdiction: "EU",
            scope: "formula",
            source: "formula input",
            sourceQuotes: [
              {
                label: `Unverified concentrations · ${ingredientFacts.unverifiedConcentrations.length}`,
                source: "formula input",
                sourceUrl: "",
                quote: `${ingredientFacts.unverifiedConcentrations.length} ingredient${ingredientFacts.unverifiedConcentrations.length === 1 ? "" : "s"} lack a convertible concentration unit — ${ingredientFacts.unverifiedConcentrations.slice(0, 5).map((i) => i.ingredient_name).join(", ")}${ingredientFacts.unverifiedConcentrations.length > 5 ? ", …" : ""}. Annex III concentration limits cannot be evaluated without verified percent/ppm/mg/g values.`,
              },
              EU_ARTICLE_10_SAFETY_ASSESSMENT_QUOTE,
              EU_ARTICLE_14_RESTRICTED_QUOTE,
            ],
            highlight: ingredientFacts.unverifiedConcentrations.map((i) => i.ingredient_name),
            severity: "error",
            blocking: true,
            message: "EU compliance cannot be determined until all ingredient concentrations include explicit supported units.",
          }),
        ]
      : []),
    ...(regulatoryIngredients.length > 0
      ? euUnmatchedIngredients.map(({ ingredient }) => {
          const chemicalQuote = {
            label: `CosIng · ${ingredient.ingredient_name}`,
            source: "CosIng",
            sourceUrl: "https://single-market-economy.ec.europa.eu/sectors/cosmetics/cosmetic-ingredient-database_en",
            quote: `${ingredient.ingredient_name}${ingredient.cas_number && ingredient.cas_number !== "N/A" ? ` (CAS ${ingredient.cas_number})` : ""} is not present in the loaded CosIng snapshot. An INCI identity cannot be confirmed, and the EU ingredient list cannot be finalised without a CosIng match.`,
          };
          return createIssue({
            code: "EU_COSING_IDENTITY_MISSING",
            jurisdiction: "EU",
            scope: "formula",
            source: "CosIng",
            sourceUrl: "https://single-market-economy.ec.europa.eu/sectors/cosmetics/cosmetic-ingredient-database_en",
            sourceQuotes: [chemicalQuote, EU_COSING_DATABASE_QUOTE, EU_ARTICLE_19_INGREDIENT_LIST_QUOTE],
            highlight: [ingredient.ingredient_name, ingredient.cas_number].filter((v) => v && v !== "N/A"),
            message: `${ingredient.ingredient_name} — not in CosIng registry`,
          });
        })
      : []),
    ...(missingDocuments.includes("CPSR")
      ? [
          createIssue({
            code: "EU_PIF_MISSING_CPSR",
            jurisdiction: "EU",
            scope: "pif",
            source: "Article 11 / Annex I",
            sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02009R1223-20240424",
            sourceQuotes: [EU_ARTICLE_10_SAFETY_ASSESSMENT_QUOTE, EU_ANNEX_I_CPSR_QUOTE, EU_ARTICLE_11_PIF_QUOTE],
            message: "Missing CPSR for PIF",
          }),
        ]
      : []),
    ...(missingDocuments.includes("Claims Substantiation")
      ? [
          createIssue({
            code: "EU_PIF_MISSING_CLAIMS_SUPPORT",
            jurisdiction: "EU",
            scope: "pif",
            source: "Article 11",
            sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02009R1223-20240424",
            sourceQuotes: [EU_ARTICLE_11_PIF_QUOTE, EU_ARTICLE_20_CLAIMS_QUOTE],
            message: "Missing claims substantiation",
          }),
        ]
      : []),
    ...relevantEuChanges.map((change) => {
      const changeQuote = {
        label: `Regulatory change · ${change.entityKey || "entity"}`,
        source: change.sourceType || "EU source",
        sourceUrl: change.sourceUrl || "",
        quote: change.summary || `A regulatory change affecting ${change.entityKey} was detected. Impact level: ${change.impactLevel || "unspecified"}.`,
      };
      return createIssue({
        code: `EU_${String(change.changeType || "REGULATORY_REVIEW").toUpperCase()}`,
        jurisdiction: "EU",
        scope: change.changeType?.includes("label") ? "label" : "formula",
        source: change.sourceType || "EU source",
        sourceUrl: change.sourceUrl || "",
        sourceQuotes: [changeQuote, EU_ARTICLE_14_RESTRICTED_QUOTE],
        highlight: [change.entityKey, ...(change.relatedNames || [])].filter(Boolean),
        severity: change.impactLevel === "high" ? "error" : "warning",
        blocking: change.impactLevel === "high",
        message: change.summary,
      });
    }),
    ...annexMatches.map(({ ingredient, rule }) => {
      const concentration = Number(ingredient.formula_percentage);
      const rawMaxPercentage = rule.maxPercentage;
      const maxPercentage = rawMaxPercentage != null ? Number(rawMaxPercentage) : NaN;
      const hasConcentrationLimit = Number.isFinite(maxPercentage) && maxPercentage > 0;
      const isProhibited = rule.ruleType === "prohibited";
      const concentrationVerified = ingredient.concentration_verified === true;
      const exceedsRestriction =
        rule.ruleType === "restricted" &&
        hasConcentrationLimit &&
        concentrationVerified &&
        Number.isFinite(concentration) &&
        concentration > maxPercentage;
      const warningThreshold = Number(rule.warningThresholdPercentage);
      const requiresWarning =
        rule.ruleType === "restricted" &&
        hasConcentrationLimit &&
        concentrationVerified &&
        Number.isFinite(concentration) &&
        Number.isFinite(warningThreshold) &&
        concentration > warningThreshold &&
        !exceedsRestriction;

      const chemicalHighlight = [
        ingredient.ingredient_name,
        rule.inciName,
        rule.substanceName,
        ...(rule.casNumbers || []),
      ].filter(Boolean);
      const chemicalQuote = buildAnnexQuote(rule, ingredient);

      if (isProhibited) {
        return createIssue({
          code: "EU_ANNEX_II_PROHIBITED",
          jurisdiction: "EU",
          scope: "formula",
          source: `EUR-Lex Annex ${rule.annex} ref ${rule.referenceNumber}`,
          sourceUrl: rule.sourceUrl || "",
          sourceQuote: rule.sourceQuote || "",
          sourceQuotes: [chemicalQuote, EU_ARTICLE_14_PROHIBITION_QUOTE],
          highlight: chemicalHighlight,
          severity: "critical",
          blocking: true,
          message: `${ingredient.ingredient_name} — prohibited (Annex II)`,
        });
      }

      if (exceedsRestriction) {
        return createIssue({
          code: "EU_ANNEX_III_LIMIT_EXCEEDED",
          jurisdiction: "EU",
          scope: "formula",
          source: `EUR-Lex Annex ${rule.annex} ref ${rule.referenceNumber}`,
          sourceUrl: rule.sourceUrl || "",
          sourceQuote: rule.sourceQuote || "",
          sourceQuotes: [chemicalQuote, EU_ARTICLE_14_RESTRICTED_QUOTE],
          highlight: chemicalHighlight,
          severity: "high",
          blocking: true,
          message: `${ingredient.ingredient_name} ${concentration}% exceeds ${maxPercentage}% limit`,
        });
      }

      // Annex IV / V / VI — positive-list concentration overrun.
      if (
        ["IV", "V", "VI"].includes(rule.annex) &&
        hasConcentrationLimit &&
        concentrationVerified &&
        Number.isFinite(concentration) &&
        concentration > maxPercentage
      ) {
        const code = `EU_ANNEX_${rule.annex}_LIMIT_EXCEEDED`;
        const categoryLabel = {
          IV: "colorant",
          V: "preservative",
          VI: "UV filter",
        }[rule.annex];
        return createIssue({
          code,
          jurisdiction: "EU",
          scope: "formula",
          source: `EUR-Lex Annex ${rule.annex} ref ${rule.referenceNumber}`,
          sourceUrl: rule.sourceUrl || "",
          sourceQuote: rule.sourceQuote || "",
          sourceQuotes: [chemicalQuote, EU_ARTICLE_14_RESTRICTED_QUOTE],
          highlight: chemicalHighlight,
          severity: "high",
          blocking: true,
          message: `${ingredient.ingredient_name} ${concentration}% exceeds Annex ${rule.annex} ${categoryLabel} max ${maxPercentage}%`,
        });
      }

      if (rule.ruleType === "restricted" && hasConcentrationLimit && !concentrationVerified) {
        return createIssue({
          code: "EU_ANNEX_III_CONCENTRATION_UNVERIFIED",
          jurisdiction: "EU",
          scope: "formula",
          source: `EUR-Lex Annex ${rule.annex} ref ${rule.referenceNumber}`,
          sourceUrl: rule.sourceUrl || "",
          sourceQuote: rule.sourceQuote || "",
          sourceQuotes: [chemicalQuote, EU_ARTICLE_14_RESTRICTED_QUOTE],
          highlight: chemicalHighlight,
          severity: "high",
          blocking: true,
          message: `${ingredient.ingredient_name} matches Annex III, but concentration is unverified`,
        });
      }

      if (requiresWarning) {
        return createIssue({
          code: "EU_ANNEX_III_WARNING_REQUIRED",
          jurisdiction: "EU",
          scope: "label",
          source: `EUR-Lex Annex ${rule.annex} ref ${rule.referenceNumber}`,
          sourceUrl: rule.sourceUrl || "",
          sourceQuote: rule.sourceQuote || "",
          sourceQuotes: [chemicalQuote, EU_ARTICLE_14_RESTRICTED_QUOTE],
          highlight: chemicalHighlight,
          severity: "medium",
          blocking: false,
          message: `${ingredient.ingredient_name} ${concentration}% — review label warning`,
        });
      }

      return null;
    }).filter(Boolean),
    ...positiveListViolations.map(({ ingredient, category, expectedAnnex }) => {
      const categoryLabel = {
        IV: "colorant (Annex IV)",
        V: "preservative (Annex V)",
        VI: "UV filter (Annex VI)",
      }[expectedAnnex];
      const chemicalHighlight = [ingredient.ingredient_name, ingredient.cas_number].filter(
        (v) => v && v !== "N/A"
      );
      const chemicalQuote = {
        label: `Positive-list check · ${ingredient.ingredient_name}`,
        source: `EUR-Lex Annex ${expectedAnnex}`,
        sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:02009R1223-20240424",
        quote: `Reg. (EC) No 1223/2009 Article 14(1) — only ${categoryLabel}s listed in Annex ${expectedAnnex} may be used in cosmetic products. ${ingredient.ingredient_name}${ingredient.cas_number && ingredient.cas_number !== "N/A" ? ` (CAS ${ingredient.cas_number})` : ""} is identified as a ${category} but is not on Annex ${expectedAnnex}.`,
      };
      return createIssue({
        code: `EU_ANNEX_${expectedAnnex}_NOT_PERMITTED`,
        jurisdiction: "EU",
        scope: "formula",
        source: `EUR-Lex Annex ${expectedAnnex}`,
        sourceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:02009R1223-20240424",
        sourceQuote: chemicalQuote.quote,
        sourceQuotes: [chemicalQuote, EU_ARTICLE_14_PROHIBITION_QUOTE],
        highlight: chemicalHighlight,
        severity: "critical",
        blocking: true,
        message: `${ingredient.ingredient_name} — ${category} not on Annex ${expectedAnnex} positive list`,
      });
    }),
    ...euSourceHealth
      .filter((source) =>
        (source.status === "Blocked" || source.status === "Error") &&
        source.previouslyHealthy
      )
      .map((source) =>
        createIssue({
          code: "EU_SOURCE_SYNC_REVIEW",
          jurisdiction: "EU",
          scope: "regulatory",
          source: source.label,
          sourceQuotes: [
            {
              label: `Source status · ${source.label}`,
              source: source.label,
              sourceUrl: "",
              quote: `${source.label} last sync status is "${source.status}". Evaluations are falling back to the previously healthy snapshot. Findings should be re-verified once live access is restored.`,
            },
            SOURCE_CONTINUITY_QUOTE,
          ],
          highlight: [source.label],
          severity: "warning",
          blocking: false,
          message: `${source.label} sync ${source.status.toLowerCase()} — using snapshot`,
        })
      ),
    ...ingredientFacts.allergenDetails.map((allergen) => {
      const chemicalQuote = {
        label: `Annex III allergen · ${titleCase(allergen.inciName)}`,
        source: allergen.source,
        sourceUrl: allergen.sourceUrl,
        quote: `${titleCase(allergen.inciName)} (CAS ${allergen.cas}) is listed as an allergenic fragrance substance whose presence must be indicated on the product label when its concentration exceeds 0.001% in leave-on products or 0.01% in rinse-off products.`,
      };
      return createIssue({
        code: "EU_LABEL_ALLERGEN_REVIEW",
        jurisdiction: "EU",
        scope: "label",
        source: allergen.source,
        sourceUrl: allergen.sourceUrl,
        sourceQuote: allergen.sourceQuote,
        sourceQuotes: [chemicalQuote, EU_ARTICLE_19_ALLERGEN_QUOTE],
        highlight: [titleCase(allergen.inciName), allergen.cas],
        severity: "warning",
        blocking: false,
        message: `${titleCase(allergen.inciName)} — declare on label`,
      });
    }),
  ];

  const mocraEvaluation = evaluateMocraCompliance({
    product,
    ifraResults,
    documents: productDocuments,
    vendors,
    euAllergenDetails: ingredientFacts.allergenDetails,
  });

  const usIssues = [
    ...(ingredientFacts.invalidIngredients.length
      ? [
          createIssue({
            code: "US_INGREDIENT_REVIEW",
            jurisdiction: "US",
            scope: "formula",
            source: "FDA · Cosmetic ingredient identity",
            sourceUrl: US_FDA_COSMETIC_IDENTITY_QUOTE.sourceUrl,
            sourceQuotes: [
              {
                label: `Unidentified ingredients · ${ingredientFacts.invalidIngredients.length}`,
                source: "Internal policy",
                sourceUrl: "",
                quote: `${ingredientFacts.invalidIngredients.length} ingredient${ingredientFacts.invalidIngredients.length === 1 ? "" : "s"} in this formula — ${ingredientFacts.invalidIngredients.slice(0, 5).map((i) => i.ingredient_name).join(", ")}${ingredientFacts.invalidIngredients.length > 5 ? ", …" : ""} — could not be normalised to a recognised cosmetic ingredient identity.`,
              },
              US_FDA_COSMETIC_IDENTITY_QUOTE,
            ],
            highlight: ingredientFacts.invalidIngredients.map((i) => i.ingredient_name),
            message: "US review required because at least one ingredient is not normalized.",
          }),
        ]
      : []),
    ...(ingredientFacts.unverifiedConcentrations.length
      ? [
          createIssue({
            code: "US_CONCENTRATION_UNVERIFIED",
            jurisdiction: "US",
            scope: "formula",
            source: "formula input",
            sourceQuotes: [
              {
                label: `Unverified concentrations · ${ingredientFacts.unverifiedConcentrations.length}`,
                source: "formula input",
                sourceUrl: "",
                quote: `${ingredientFacts.unverifiedConcentrations.length} ingredient${ingredientFacts.unverifiedConcentrations.length === 1 ? "" : "s"} — ${ingredientFacts.unverifiedConcentrations.slice(0, 5).map((i) => i.ingredient_name).join(", ")}${ingredientFacts.unverifiedConcentrations.length > 5 ? ", …" : ""} — lack convertible concentration units. Required before US safety substantiation can be completed.`,
              },
              US_MOCRA_SAFETY_SUBSTANTIATION_QUOTE,
            ],
            highlight: ingredientFacts.unverifiedConcentrations.map((i) => i.ingredient_name),
            message: "US review cannot be completed until all ingredient concentrations include explicit supported units.",
          }),
        ]
      : []),
    ...(missingDocuments.includes("Claims Substantiation")
      ? [
          createIssue({
            code: "US_CLAIMS_SUPPORT_REVIEW",
            jurisdiction: "US",
            scope: "claims",
            source: "FTC Act § 5",
            sourceUrl: US_FTC_CLAIMS_QUOTE.sourceUrl,
            sourceQuotes: [US_FTC_CLAIMS_QUOTE],
            severity: "warning",
            blocking: false,
            message: "US claims draft should not be released before substantiation is linked.",
          }),
        ]
      : []),
    ...mocraEvaluation.issues,
    ...usSourceHealth
      .filter((source) =>
        (source.status === "Blocked" || source.status === "Error") &&
        source.previouslyHealthy
      )
      .map((source) =>
        createIssue({
          code: "US_SOURCE_SYNC_REVIEW",
          jurisdiction: "US",
          scope: "regulatory",
          source: source.label,
          sourceQuotes: [
            {
              label: `Source status · ${source.label}`,
              source: source.label,
              sourceUrl: "",
              quote: `${source.label} last sync status is "${source.status}". Evaluations are falling back to the previously healthy snapshot. Findings should be re-verified once live access is restored.`,
            },
            SOURCE_CONTINUITY_QUOTE,
          ],
          highlight: [source.label],
          severity: "warning",
          blocking: false,
          message: `${source.label} sync ${source.status.toLowerCase()} — using snapshot`,
        })
      ),
  ];

  const labelBase = {
    productName: product.name,
    function: product.function,
    netContent: product.netContent,
    batchFormat: product.packaging?.batchFormat || "TBD",
    responsiblePerson: product.responsiblePerson,
    responsibleAddress: product.responsibleAddress,
    countryOfOrigin: product.countryOfOrigin,
    ingredients: labelIngredients,
    allergens: ingredientFacts.allergens,
  };

  const euLabelDraft = {
    ...labelBase,
    jurisdiction: "EU",
    pao: product.packaging?.pao || "",
    minimumDurability: product.packaging?.minimumDurability || "",
    warnings: ingredientFacts.allergens.length
      ? [`Review allergen declaration for ${ingredientFacts.allergens.join(", ")}`]
      : [],
  };

  const usLabelDraft = {
    ...labelBase,
    jurisdiction: "US",
    pao: "",
    minimumDurability: product.packaging?.minimumDurability || "",
    warnings: usIssues.filter((issue) => issue.scope === "label" || issue.scope === "claims").map((issue) => issue.message),
  };

  const pifDraft = {
    productDescription: `${product.brand} ${product.name} ${product.function}`,
    requiredDocuments: requiredPifDocuments,
    availableDocuments: productDocuments
      .filter((document) => requiredPifDocuments.includes(document.type))
      .map((document) => document.type),
    missingDocuments: requiredPifDocuments.filter((type) => !documentTypes.includes(type)),
    claims: product.claims || [],
    retentionPolicy: "10 years after last batch placed on market",
    ready: requiredPifDocuments.every((type) => documentTypes.includes(type)),
  };

  const jurisdictions = {
    IFRA: {
      status: ifraIssues.some((issue) => issue.blocking) ? "Review" : "Clear",
      issues: ifraIssues,
      facts: {
        validIngredients: ingredientFacts.validIngredients.length,
        unverifiedConcentrations: ingredientFacts.unverifiedConcentrations.length,
        thresholdBreaches: ingredientFacts.thresholdBreaches.length,
      },
    },
    EU: {
      status: euIssues.some((issue) => issue.blocking) ? "Review" : "Clear",
      issues: euIssues,
    facts: {
      markets: product.markets?.includes("EU") ? ["EU"] : [],
      missingDocuments,
      allergens: ingredientFacts.allergens,
      unverifiedConcentrations: ingredientFacts.unverifiedConcentrations.map(
        (ingredient) => ingredient.ingredient_name
      ),
      matchedRegistryIngredients: euMatchedIngredients.length,
      unmatchedRegistryIngredients: euUnmatchedIngredients.map(
        ({ ingredient }) => ingredient.ingredient_name
      ),
      activeIngredientNames: labelIngredients,
      sourceHealth: euSourceHealth,
      liveChangeCount: relevantEuChanges.length,
      annexRuleMatches: annexMatches.length,
      ruleSet: {
        complete: euRuleSet.complete,
        source: euRuleSet.source,
        counts: euRuleSet.counts,
        metadata: euRuleSet.metadata,
      },
      },
    },
    US: {
      status: usIssues.some((issue) => issue.blocking) ? "Review" : "Clear",
      issues: usIssues,
      facts: {
        markets: product.markets?.includes("US") ? ["US"] : [],
        allergens: ingredientFacts.allergens,
        unverifiedConcentrations: ingredientFacts.unverifiedConcentrations.map(
          (ingredient) => ingredient.ingredient_name
        ),
        mocra: mocraEvaluation.facts,
        sourceHealth: usSourceHealth,
      },
    },
  };

  const allIssues = Object.values(jurisdictions).flatMap((entry) => entry.issues);
  const blockingIssueCount = allIssues.filter((issue) => issue.blocking).length;

  return {
    activeFormula: {
      ...activeFormula,
      parsedResponse,
      ifraResults,
      unfolded,
    },
    jurisdictions,
    labelDrafts: {
      EU: euLabelDraft,
      US: usLabelDraft,
    },
    pifDraft,
    overallStatus: blockingIssueCount > 0 ? "Review" : "Ready",
    issueCount: allIssues.length,
    blockingIssueCount,
  };
};

export const buildPortfolioSnapshot = (
  products,
  documents,
  {
    regulatoryIngredients = [],
    regulatoryChanges = [],
    regulatorySources = [],
    regulatoryRules = [],
    vendors = [],
  } = {}
) => {
  const evaluatedProducts = products.map((product) => ({
    ...product,
    compliance: buildProductCompliance(product, documents, {
      regulatoryIngredients,
      regulatoryChanges,
      regulatorySources,
      regulatoryRules,
      vendors,
    }),
  }));

  const allIssues = evaluatedProducts.flatMap((product) =>
    Object.entries(product.compliance.jurisdictions).flatMap(([jurisdiction, entry]) =>
      entry.issues.map((issue) => ({
        ...issue,
        productId: product.id,
        productName: product.name,
        jurisdiction,
      }))
    )
  );

  return {
    evaluatedProducts,
    allIssues,
  };
};
